"use client";

import type { SchoolData } from "@nimble/candidate-api/types";
import { useIsMobile } from "@nimble/react-state-hooks/use-mobile";
import dynamic from "next/dynamic";
import React, { useState } from "react";

import { MapPopupType } from "@/components/shared/map/types";

// Dynamically import MapView with SSR disabled
const MapView = dynamic(() => import("@/components/shared/map/map.view"), {
  ssr: false,
});

export type MapContainerProps = {
  schools: SchoolData[];
  selectedSchool?: SchoolData;
  center: [number, number];
  radius?: number; // Radius in miles (5, 10, 25, 50, 100)
  zoomOffset?: number;
  showSchoolCount?: boolean;
} & MapPopupType;

export function getDesktopZoomFromRadius(radius: number | undefined): number {
  if (!radius) return 10;

  if (radius <= 5) return 12;
  if (radius <= 10) return 11;
  if (radius <= 25) return 10;
  if (radius <= 50) return 9;
  if (radius <= 100) return 8;

  return 7;
}

export function getMobileZoomFromRadius(radius: number | undefined): number {
  return getDesktopZoomFromRadius(radius) - 1;
}

export default function MapContainer({
  schools,
  selectedSchool,
  center,
  radius,
  zoomOffset = 0,
  PopupComponent,
  showSchoolCount,
}: MapContainerProps) {
  const [sliderSchool, setSliderSchool] = useState<SchoolData | null>(null);
  const isMobile = useIsMobile();

  const baseZoom = isMobile
    ? getMobileZoomFromRadius(radius)
    : getDesktopZoomFromRadius(radius);
  const zoom = baseZoom + zoomOffset;

  // Use external selectedSchool if provided, otherwise use internal state
  const currentSelectedSchool = selectedSchool || sliderSchool || undefined;

  const handleMarkerClick = (school: SchoolData) => {
    // On mobile, show slider
    if (isMobile) {
      setSliderSchool(school);
    }
    // On desktop, Leaflet popup will handle the display automatically
  };

  const handleCloseSelectedSchool = () => {
    setSliderSchool(null);
  };

  return (
    <MapView
      schools={schools}
      selectedSchool={currentSelectedSchool}
      center={center}
      zoom={zoom}
      radius={radius}
      onCloseSelectedSchool={
        selectedSchool ? () => {} : handleCloseSelectedSchool
      }
      onMarkerClick={handleMarkerClick}
      showSchoolCount={showSchoolCount ?? true}
      PopupComponent={PopupComponent}
    />
  );
}
